/**
 * CompanionFeedOverlay — web port. Full-screen drawer that renders the
 * companion feed (cards from voice / text / web data) with a footer
 * input for sending a text message.
 */

import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ChangeEvent,
} from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../common/Icon';
import Spinner from '../../common/Spinner';
import WebView from '../../common/WebView';
import MenuRecommendationsCard from './MenuRecommendationsCard';
import SceneInfoCard from './SceneInfoCard';
import type {
  CompanionCard,
  CompanionSession,
  CardAction,
} from '../../types/companion';
import './CompanionFeedOverlay.css';

interface Props {
  visible: boolean;
  onClose: () => void;
  cards: CompanionCard[];
  sessions: CompanionSession[];
  isVoiceActive?: boolean;
  onSendText: (text: string) => Promise<void> | void;
  onCardAction?: (action: CardAction) => void;
  autoFocusInput?: boolean;
  agentDisplayName?: string;
  /** Show the camera/upload button (gate on live_vision || dining_concierge). */
  visionEnabled?: boolean;
  /** Called with a captured/uploaded still image to scan. */
  onScanImage?: (image: { data: string; mime: string }) => Promise<void> | void;
  /** Realtime video (Gemini Live) available — shows the live camera button
   * (gate on live_vision_realtime + an active voice session). */
  realtimeEnabled?: boolean;
  /** Open the realtime live camera overlay. */
  onStartLiveVideo?: () => void;
  /** Open the snapshot live-camera overlay (tap-to-identify). */
  onOpenLiveCamera?: () => void;
}

function renderCard(
  card: CompanionCard,
  onCardAction?: (action: CardAction) => void,
) {
  const c = card.content;
  switch (card.kind) {
    case 'text':
      return (
        <p className="wg-companion-card-body">{c?.text || c?.body || ''}</p>
      );
    case 'system':
      return <span>{c?.text || c?.body || ''}</span>;
    case 'image':
      return c?.url || c?.uri ? (
        <img src={c.url || c.uri} alt={c.alt || ''} />
      ) : null;
    case 'web':
      return c?.uri || c?.html ? (
        <WebView
          source={{ uri: c.uri, html: c.html }}
          title="Web preview"
          style={{ minHeight: 280 }}
        />
      ) : null;
    case 'card':
      return (
        <>
          {c?.title && <h4 className="wg-companion-card-title">{c.title}</h4>}
          {c?.description && (
            <p className="wg-companion-card-body">{c.description}</p>
          )}
          {card.actions && card.actions.length > 0 && (
            <div className="wg-companion-card-actions">
              {card.actions.map((a, i) => (
                <button
                  key={i}
                  type="button"
                  className="wg-companion-card-action"
                  onClick={() => onCardAction?.(a)}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </>
      );
    case 'map':
      return (
        <p className="wg-companion-card-body">
          {c?.caption || `Location: ${c?.latitude}, ${c?.longitude}`}
        </p>
      );
    case 'action':
      return (
        <div className="wg-companion-card-actions">
          {(c?.actions || []).map((a: any, i: number) => (
            <button
              key={i}
              type="button"
              className="wg-companion-card-action"
              onClick={() => onCardAction?.(a)}
            >
              {a.label}
            </button>
          ))}
        </div>
      );
    case 'menu_recommendations':
      return <MenuRecommendationsCard item={c} />;
    case 'scene_info':
      return <SceneInfoCard item={c} />;
    default:
      return null;
  }
}

export default function CompanionFeedOverlay({
  visible,
  onClose,
  cards,
  onSendText,
  onCardAction,
  autoFocusInput = false,
  agentDisplayName,
  visionEnabled = false,
  onScanImage,
  isVoiceActive = false,
  realtimeEnabled = false,
  onStartLiveVideo,
  onOpenLiveCamera,
}: Props) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [scanning, setScanning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const feedRef = useRef<HTMLDivElement>(null);

  const handleFilePicked = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file || !onScanImage) return;
    setScanning(true);
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      const base64 = dataUrl.split(',')[1] || '';
      await onScanImage({ data: base64, mime: file.type || 'image/jpeg' });
    } finally {
      setScanning(false);
    }
  };

  useEffect(() => {
    if (visible && autoFocusInput) {
      const t = setTimeout(() => inputRef.current?.focus(), 200);
      return () => clearTimeout(t);
    }
  }, [visible, autoFocusInput]);

  useEffect(() => {
    feedRef.current?.scrollTo({
      top: feedRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [cards.length]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, onClose]);

  if (!visible) return null;

  const handleSend = async (e?: FormEvent) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    setText('');
    try {
      await onSendText(t);
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div className="wg-companion-overlay" role="dialog" aria-modal="true">
      <div className="wg-companion-topbar">
        <h3 className="wg-companion-title">
          {agentDisplayName || 'Companion'}
        </h3>
        <button
          type="button"
          className="wg-companion-close"
          onClick={onClose}
          aria-label="Close"
        >
          <Icon name="close" size={20} color="#fff" />
        </button>
      </div>

      <div className="wg-companion-feed" ref={feedRef}>
        {cards.length === 0 ? (
          <div className="wg-companion-empty">
            Nothing here yet. Speak or type to get started.
          </div>
        ) : (
          cards.map(card => (
            <div key={card.id} className={`wg-companion-card ${card.kind}`}>
              {renderCard(card, onCardAction)}
            </div>
          ))
        )}
      </div>

      <form className="wg-companion-footer" onSubmit={handleSend}>
        <div className="wg-companion-input-row">
          {visionEnabled && onScanImage && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleFilePicked}
              />
              <button
                type="button"
                className="wg-companion-send"
                disabled={scanning || sending}
                aria-label="Scan with camera"
                onClick={() => fileInputRef.current?.click()}
              >
                {scanning ? (
                  <Spinner size="small" />
                ) : (
                  <Icon
                    name="scan-outline"
                    size={20}
                    color="rgba(255,255,255,0.9)"
                  />
                )}
              </button>
            </>
          )}
          {visionEnabled && onOpenLiveCamera && (
            <button
              type="button"
              className="wg-companion-send"
              aria-label="Live camera"
              onClick={onOpenLiveCamera}
            >
              <Icon
                name="camera-outline"
                size={20}
                color="rgba(255,255,255,0.9)"
              />
            </button>
          )}
          {realtimeEnabled && isVoiceActive && onStartLiveVideo && (
            <button
              type="button"
              className="wg-companion-send"
              aria-label="Live camera"
              onClick={onStartLiveVideo}
            >
              <Icon
                name="videocam-outline"
                size={20}
                color="rgba(255,255,255,0.9)"
              />
            </button>
          )}
          <input
            ref={inputRef}
            className="wg-companion-input"
            type="text"
            placeholder="Ask anything…"
            value={text}
            onChange={e => setText(e.target.value)}
            disabled={sending}
          />
          <button
            type="submit"
            className="wg-companion-send"
            disabled={!text.trim() || sending}
            aria-label="Send"
          >
            <Icon name="arrow-up" size={20} color="rgba(255,255,255,0.9)" />
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
